/* SelectorDropdown — Frosted glass trigger + custom dropdown menu */

/* Wrapper for positioning */
.wrapper {
  position: relative;
  display: inline-flex;
}

/* Trigger button — uses design system surface color */
.trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  background: var(--bg-hover);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  box-shadow:
    0 8px 4px rgba(0, 0, 0, 0.1),
    0 4px 2px rgba(0, 0, 0, 0.1),
    0 2px 1px rgba(0, 0, 0, 0.1),
    0 1px 0.5px rgba(0, 0, 0, 0.1),
    0 1px 0.5px rgba(0, 0, 0, 0.1);
  transition: background var(--duration-fast);
}

.trigger:hover {
  background: var(--bg-active);
}

.trigger:focus-visible {
  outline: 2px solid var(--accent-secondary);
  outline-offset: 2px;
}

/* Icon inside trigger (platform logo, flag, calendar) */
.triggerIcon {
  width: 20px;
  height: 20px;
  border-radius: 2px;
  object-fit: contain;
  flex-shrink: 0;
  background: none;
  overflow: hidden;
}

/* Label text */
.triggerLabel {
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 700;
  color: #c3c3c3;
  text-transform: uppercase;
  white-space: nowrap;
  line-height: 17.5px;
}

/* Chevron arrow */
.triggerChevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: transform var(--duration-fast);
}

.triggerChevron svg {
  width: 100%;
  height: 100%;
  color: #969696;
}

.wrapper[data-open="true"] .triggerChevron {
  transform: rotate(180deg);
}

/* Dropdown panel */
.dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 208px;
  background: var(--bg-elevated);
  border-radius: 5px;
  overflow: hidden;
  z-index: 100;
  box-shadow:
    0 24px 24px rgba(0, 0, 0, 0.1),
    0 16px 16px rgba(0, 0, 0, 0.1),
    0 6px 6px rgba(0, 0, 0, 0.1),
    0 3px 3px rgba(0, 0, 0, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.1);
  padding-bottom: 10px;
  animation: dropIn var(--duration-fast) var(--ease-default);
}

@keyframes dropIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Section header inside dropdown */
.dropdownHeader {
  padding: 6px 12px;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 21px;
  height: 32px;
  display: flex;
  align-items: center;
}

/* Menu item */
.item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px 0 14px;
  cursor: pointer;
  transition: background var(--duration-fast);
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
}

.item:hover {
  background: var(--bg-hover);
}

/* Left status bar (default: subtle) */
.item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255, 255, 255, 0.2);
}

/* Selected item */
.itemSelected {
  background: var(--bg-hover);
}

.itemSelected::before {
  background: var(--accent-primary-hover);
  box-shadow: 0 2px 10px rgba(227, 40, 76, 0.6);
}

/* Item icon (platform avatar) */
.itemIcon {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
}

/* Item label */
.itemLabel {
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 400;
  color: var(--text-primary);
  line-height: 17.5px;
  white-space: nowrap;
}

.itemSelected .itemLabel {
  font-weight: 700;
}

/* Backdrop for closing */
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

/* ── Inline variant ────────────────────────────────────────
   The trigger becomes a word in a sentence: no chrome at rest, an underline
   on hover so it still reads as something you can act on. Used by the canvas
   scope line, where three chips above the document looked like a form. */

.triggerInline {
  height: auto;
  padding: 2px 2px;
  gap: 3px;
  background: none;
  box-shadow: none;
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
  text-transform: none;
  letter-spacing: normal;
  border-bottom: 1px dashed var(--border-strong);
}

.triggerInline:hover {
  background: none;
  color: var(--text-primary);
  border-bottom-color: var(--ai-accent);
}

.triggerInline .triggerChevron {
  opacity: 0.5;
  width: 12px;
  height: 12px;
}

.triggerInline .triggerIcon {
  width: 16px;
  height: 16px;
}
